/**
 * Header'daki site içi global arama (komut paleti).
 *
 * - "yeni fatura", "kasa ekleme" gibi yazımlarla sayfa + hızlı eylem arar.
 * - Türkçe karakter toleranslı (globalSearchIndex.normalizeSearchText).
 * - Klavye: ↑/↓ gezinme, Enter git, Esc kapat; Ctrl+K (⌘K) ile odaklanır.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  InputBase,
  Paper,
  Popper,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Chip,
  ClickAwayListener,
  styled,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import BoltIcon from '@mui/icons-material/Bolt';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { searchEntries, normalizeSearchText, type SearchResult } from './globalSearchIndex';
import { useAuth } from '../../contexts/AuthContext';
import { isPathAllowedForSubAdmin } from '../users/adminModuleMap';

const SearchContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.12)',
  border: '1px solid rgba(255, 255, 255, 0.25)',
  borderRadius: 20,
  height: 36,
  paddingLeft: theme.spacing(1.25),
  paddingRight: theme.spacing(1),
  width: 280,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderColor: 'rgba(255, 255, 255, 0.4)',
  },
  '&:focus-within': {
    backgroundColor: 'rgba(255, 255, 255, 0.22)',
    borderColor: 'rgba(255, 255, 255, 0.6)',
    width: 380,
  },
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const SearchInput = styled(InputBase)(() => ({
  flex: 1,
  color: 'var(--sidebar-text-color, white)',
  fontSize: '0.85rem',
  '& .MuiInputBase-input::placeholder': {
    color: 'var(--sidebar-text-color, white)',
    opacity: 0.65,
  },
}));

const ShortcutChip = styled(Box)(() => ({
  fontSize: '0.65rem',
  fontWeight: 600,
  color: 'var(--sidebar-text-color, white)',
  opacity: 0.55,
  border: '1px solid rgba(255, 255, 255, 0.35)',
  borderRadius: 5,
  padding: '1px 6px',
  whiteSpace: 'nowrap',
  userSelect: 'none',
}));

/** Sorgu geçen kısımları vurgular (normalize eşleşme; TR karakter toleranslı). */
const HighlightedText: React.FC<{ text: string; query: string }> = ({ text, query }) => {
  const nQuery = normalizeSearchText(query);
  const tokens = nQuery.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return <>{text}</>;

  // Normalize edilmiş metinde token konumlarını bul, orijinal metne uygula.
  // normalizeSearchText karakter sayısını korur (birebir replace), bu yüzden
  // indeksler orijinal metinle hizalıdır.
  const nText = normalizeSearchText(text);
  const marks = new Array(text.length).fill(false);
  for (const token of tokens) {
    let idx = nText.indexOf(token);
    while (idx !== -1) {
      for (let i = idx; i < Math.min(idx + token.length, text.length); i++) marks[i] = true;
      idx = nText.indexOf(token, idx + 1);
    }
  }

  const parts: React.ReactNode[] = [];
  let buf = '';
  let bufMarked = marks[0] ?? false;
  for (let i = 0; i < text.length; i++) {
    if (marks[i] === bufMarked) {
      buf += text[i];
    } else {
      parts.push(
        bufMarked ? <mark key={i} style={{ background: 'transparent', color: 'inherit', fontWeight: 700 }}>{buf}</mark> : buf
      );
      buf = text[i];
      bufMarked = marks[i];
    }
  }
  if (buf) {
    parts.push(
      bufMarked ? <mark key="last" style={{ background: 'transparent', color: 'inherit', fontWeight: 700 }}>{buf}</mark> : buf
    );
  }
  return <>{parts}</>;
};

const GlobalSearch: React.FC = () => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const { isSubAdmin, adminPanelPermissions } = useAuth();
  const isTr = i18n.language.startsWith('tr');
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);

  const results: SearchResult[] = useMemo(() => {
    const all = searchEntries(query, isSubAdmin ? 30 : 10);
    if (!isSubAdmin) return all;
    // Alt admin: izinsiz sayfaya arama üzerinden gidilemesin (deny-by-default).
    // Sorgu string'i/hash izin kontrolünden önce atılır.
    return all
      .filter((r) => {
        const purePath = r.entry.path.split('?')[0].split('#')[0];
        return isPathAllowedForSubAdmin(adminPanelPermissions, purePath);
      })
      .slice(0, 10);
  }, [query, isSubAdmin, adminPanelPermissions]);

  // Ctrl+K / ⌘K ile arama kutusuna odaklan
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  const closeAndClear = () => {
    setOpen(false);
    setQuery('');
  };

  const goTo = (path: string) => {
    closeAndClear();
    inputRef.current?.blur();
    navigate(path);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open || results.length === 0) {
      if (e.key === 'Escape') {
        closeAndClear();
        inputRef.current?.blur();
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % results.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((i) => (i - 1 + results.length) % results.length);
        break;
      case 'Enter':
        e.preventDefault();
        goTo(results[activeIndex].entry.path);
        break;
      case 'Escape':
        closeAndClear();
        inputRef.current?.blur();
        break;
      default:
        break;
    }
  };

  const showDropdown = open && query.trim().length > 0;

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box>
        <SearchContainer ref={containerRef}>
          <SearchIcon sx={{ fontSize: 18, mr: 0.75, opacity: 0.7, color: 'var(--sidebar-text-color, white)' }} />
          <SearchInput
            inputRef={inputRef}
            placeholder={tt('Sayfa veya işlem ara…', 'Search pages or actions…')}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKeyDown}
            inputProps={{ 'aria-label': tt('Site içi arama', 'Global search') }}
          />
          {!query && <ShortcutChip>Ctrl K</ShortcutChip>}
        </SearchContainer>

        <Popper
          open={showDropdown}
          anchorEl={containerRef.current}
          placement="bottom-start"
          style={{ zIndex: 1300 }}
          modifiers={[{ name: 'offset', options: { offset: [0, 6] } }]}
        >
          <Paper
            elevation={8}
            sx={{
              width: 380,
              maxHeight: 420,
              overflowY: 'auto',
              borderRadius: 2,
            }}
          >
            {results.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {tt('Sonuç bulunamadı', 'No results found')}
                </Typography>
              </Box>
            ) : (
              <List dense disablePadding>
                {results.map((r, idx) => {
                  const title = isTr ? r.entry.title : r.entry.titleEn;
                  return (
                    <ListItemButton
                      key={r.entry.id}
                      selected={idx === activeIndex}
                      onMouseEnter={() => setActiveIndex(idx)}
                      // onMouseDown: input blur olmadan tıklamayı yakala
                      onMouseDown={(e) => {
                        e.preventDefault();
                        goTo(r.entry.path);
                      }}
                      sx={{ py: 1 }}
                    >
                      <ListItemIcon sx={{ minWidth: 34 }}>
                        {r.entry.isAction ? (
                          <BoltIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                        ) : (
                          <DescriptionOutlinedIcon sx={{ fontSize: 18, color: 'action.active' }} />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" component="span">
                            <HighlightedText text={title} query={query} />
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary" component="span">
                            {r.entry.path}
                          </Typography>
                        }
                      />
                      <Chip
                        label={r.entry.isAction ? tt('İşlem', 'Action') : tt('Sayfa', 'Page')}
                        size="small"
                        variant="outlined"
                        color={r.entry.isAction ? 'warning' : 'default'}
                        sx={{ height: 20, fontSize: '0.65rem', ml: 1 }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            )}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default GlobalSearch;
